import { Address } from '@adornis/address/db/address.js';
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { iban } from '@adornis/validation/functions/iban.js';
import { map } from 'rxjs';

@Entity()
export class LecturerData extends MongoEntity {
  static override _class = 'LecturerData';
  static override _collectionName = 'lecturer-data';

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  override _id!: string;

  @Field(type => String)
  lecturerID: Maybe<string>;

  @Field(type => Address, { default: v => v ?? new Address({}) })
  billingAddress: Maybe<Address>;

  @validate(iban({ allowEmpty: true }))
  @Field(type => String)
  iban: Maybe<string>;

  @Field(type => String)
  salutation: Maybe<string>;

  @Field(type => String)
  phone: Maybe<string>;

  @Field(type => String)
  title: Maybe<string>;

  // buildify page about him
  @Field(type => String)
  detailPage: Maybe<string>;
}

export const getLecturerDataByLecturerID = registerQuery({
  operationName: 'getLecturerDataByID',
  type: () => LecturerData,
  params: [{ name: 'lecturerID', type: () => String }],
  resolve: (lecturerID: string) => {
    return async (gqlFields: BaseQLSelectionSet<LecturerData>) => {
      const collection = await getCollection<LecturerData>(LecturerData._class);
      const existing = await collection.findOne<LecturerData>({ lecturerID });
      return existing as Maybe<LecturerData>;
    };
  },
});

export const subscribeLecturerDataByLecturerID = registerSubscription({
  operationName: 'subscribeLecturerDataByID',
  type: () => LecturerData,
  params: [{ name: 'lecturerID', type: () => String }],
  resolve: (lecturerID: string) => {
    return async (gqlFields: BaseQLSelectionSet<LecturerData>) =>
      watchQuery(LecturerData._collectionName, { lecturerID }).pipe(map(lecturerDatas => lecturerDatas[0]));
  },
});
